import React, { useRef, useState } from 'react';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import { Download, Loader2 } from 'lucide-react';
import { FLSSafetySystemRow } from './FLSSafetySystemsTable';
import { MEPFLSInfrastructureRow } from './MEPFLSInfrastructureTable';
import { FireIncidentDrillsRow } from './FireIncidentDrillsTable';
import { ERTSafetyEquipmentRow } from './ERTSafetyEquipmentTable';
import { IncidentSummaryRow } from './IncidentSummaryTable';
import { AuditRecord } from '../../utils/syncMISToKPI';

// KPI-linked Sr.Nos — must stay in sync with syncMISToKPI.ts
const KPI_TABLE1: number[] = [1, 3, 5, 6, 8, 11, 12, 14, 16, 19, 20];
const KPI_TABLE2: number[] = [26, 29, 35, 41, 44];
const KPI_TABLE3: number[] = [45, 46, 47, 48, 49];

interface MISReportPDFProps {
  checklistName: string;
  premisesName: string;
  date: string;
  flsSafetySystems: FLSSafetySystemRow[];
  mepFLSInfrastructure: MEPFLSInfrastructureRow[];
  fireIncidentDrills: FireIncidentDrillsRow[];
  ertSafetyEquipment?: ERTSafetyEquipmentRow[];
  incidentSummary?: IncidentSummaryRow[];
  fireLicenseDate?: string;
  fireNocIssueDate?: string;
  dateOfRenewal?: string;
  statusAvailability?: string;
  externalAuditRecords?: AuditRecord[];
  internalAuditRecords?: AuditRecord[];
  buttonClass?: string;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const getStatusStyle = (status: string | null): React.CSSProperties => {
  const s = (status || '').toUpperCase();
  const base: React.CSSProperties = {
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: 600,
    fontSize: '10px',
    display: 'inline-block',
  };
  if (s === 'YES') return { ...base, background: '#dcfce7', color: '#166534' };
  if (s === 'NO') return { ...base, background: '#fee2e2', color: '#991b1b' };
  if (s === 'N/A') return { ...base, background: '#f1f5f9', color: '#475569' };
  return { ...base, background: '#fef9c3', color: '#854d0e' };
};

const getConditionStyle = (condition: string | null): React.CSSProperties => {
  const c = (condition || '').toLowerCase();
  const base: React.CSSProperties = {
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: 600,
    fontSize: '10px',
    display: 'inline-block',
  };
  if (c === 'good') return { ...base, background: '#dcfce7', color: '#166534' };
  if (c === 'fair') return { ...base, background: '#fef9c3', color: '#854d0e' };
  if (c === 'poor') return { ...base, background: '#fee2e2', color: '#991b1b' };
  return { ...base, background: '#f1f5f9', color: '#475569' };
};

// KPI-linked row highlight style
const kpiRowStyle: React.CSSProperties = {
  background: '#f0fdfa',
  borderLeft: '4px solid #14b8a6',
};

const MISReportPDF: React.FC<MISReportPDFProps> = ({
  checklistName,
  premisesName,
  date,
  flsSafetySystems,
  mepFLSInfrastructure,
  fireIncidentDrills,
  ertSafetyEquipment = [],
  incidentSummary = [],
  fireLicenseDate,
  fireNocIssueDate,
  dateOfRenewal,
  statusAvailability,
  externalAuditRecords = [],
  internalAuditRecords = [],
  buttonClass,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadPdf = async () => {
    if (!printRef.current) return;
    setIsGenerating(true);

    try {
      const wrapper = printRef.current;
      wrapper.style.display = 'block';
      wrapper.style.position = 'absolute';
      wrapper.style.left = '-9999px';
      wrapper.style.top = '0';

      await new Promise((resolve) => setTimeout(resolve, 600));

      const element = wrapper.firstElementChild as HTMLElement;
      const safeFilename = (checklistName || 'MIS_Report').replace(/[^a-zA-Z0-9_\-]/g, '_');

      const opt = {
        margin: [8, 6, 8, 6] as any,
        filename: `${safeFilename}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: 1100,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' as const },
        pagebreak: { mode: ['css', 'legacy'] },
      };

      await html2pdf().set(opt).from(element).save();

      wrapper.style.display = 'none';
      wrapper.style.position = '';
      wrapper.style.left = '';
      wrapper.style.top = '';
    } catch (error) {
      console.error('Failed to generate MIS Report PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Shared table cell style
  const tdBase: React.CSSProperties = {
    border: '1px solid #cbd5e1',
    padding: '5px 7px',
    fontSize: '10px',
    lineHeight: 1.4,
    wordBreak: 'break-word',
    verticalAlign: 'top',
  };

  const thBase: React.CSSProperties = {
    ...tdBase,
    background: '#f1f5f9',
    fontWeight: 700,
    color: '#1e293b',
    textAlign: 'center',
    verticalAlign: 'middle',
  };

  const sectionHeaderStyle = (bg: string): React.CSSProperties => ({
    background: bg,
    color: '#fff',
    fontWeight: 700,
    fontSize: '12px',
    padding: '8px 12px',
    borderRadius: '6px 6px 0 0',
    marginTop: '20px',
    marginBottom: 0,
  });

  // KPI legend badge
  const kpiBadge: React.CSSProperties = {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#14b8a6',
    marginRight: '4px',
    verticalAlign: 'middle',
  };

  return (
    <>
      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={isGenerating}
        className={
          buttonClass ||
          'flex items-center gap-2 py-2 px-4 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors shadow hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed'
        }
        title="Download MIS Report as PDF"
      >
        {isGenerating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {isGenerating ? 'Generating PDF...' : 'Download Report'}
      </button>

      {/* Hidden printable content — rendered off-screen */}
      <div ref={printRef} style={{ display: 'none' }}>
        <div
          style={{
            fontFamily: 'Arial, sans-serif',
            color: '#1a202c',
            width: '1100px',
            fontSize: '11px',
            background: '#fff',
            padding: '20px 24px',
          }}
        >
          {/* ── Report Header ── */}
          <div
            style={{
              borderBottom: '3px solid #0f766e',
              paddingBottom: '12px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 800,
                  color: '#0f766e',
                }}
              >
                Fire Safety MIS Report
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                {checklistName || 'MIS Checklist'}
              </p>
            </div>
            <div
              style={{
                textAlign: 'right',
                fontSize: '11px',
                color: '#475569',
                lineHeight: 1.7,
              }}
            >
              <div>
                <strong>Premises:</strong> {premisesName || 'N/A'}
              </div>
              <div>
                <strong>Date:</strong> {formatDate(date)}
              </div>
              <div>
                <strong>Generated:</strong> {new Date().toLocaleDateString('en-IN')}
              </div>
              {/* KPI legend */}
              <div style={{ marginTop: '6px', fontSize: '9px', color: '#475569' }}>
                <span style={kpiBadge} /> = Linked to KPI Fire Safety Scorecard
              </div>
            </div>
          </div>

          {/* ── Fire License & NOC Status ── */}
          {(fireLicenseDate || fireNocIssueDate || dateOfRenewal || statusAvailability) && (
            <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <h2 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', borderBottom: '1px solid #cbd5e1', paddingBottom: '4px', color: '#0f766e' }}>Fire License Status</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '11px' }}>
                <div style={{ width: '30%' }}><strong>Fire License Date:</strong> {fireLicenseDate || 'N/A'}</div>
                <div style={{ width: '30%' }}><strong>Fire NOC Issue Date:</strong> {fireNocIssueDate || 'N/A'}</div>
                <div style={{ width: '30%' }}><strong>Date of Renewal:</strong> <span style={{ color: dateOfRenewal && new Date(dateOfRenewal) < new Date() ? 'red' : 'inherit' }}>{dateOfRenewal || 'N/A'}</span></div>
                <div style={{ width: '30%' }}><strong>Status Availability:</strong> <span style={{ color: statusAvailability === 'No' ? 'red' : 'inherit', fontWeight: statusAvailability === 'No' ? 'bold' : 'normal' }}>{statusAvailability || 'N/A'}</span></div>
              </div>
            </div>
          )}

          {/* ── Audit Report Sections ── */}
          {(externalAuditRecords.length > 0 || internalAuditRecords.length > 0) && (
            <>
              {/* External Audit */}
              {externalAuditRecords.length > 0 && (
                <>
                  <div style={sectionHeaderStyle('#f97316')}>
                    EXTERNAL AUDIT - % OF NON COMPLIANCE STATUS REPORT
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: '8px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thBase, width: '20%', textAlign: 'left' }}>Recommendation Category</th>
                        <th style={{ ...thBase, width: '15%' }}>Total NC</th>
                        <th style={{ ...thBase, width: '15%' }}>NC Completion</th>
                        <th style={{ ...thBase, width: '13%' }}>Pending Points</th>
                        <th style={{ ...thBase, width: '15%' }}>% NC Completion</th>
                        <th style={{ ...thBase, width: '12%' }}>Current Score</th>
                        <th style={{ ...thBase, width: '10%' }}>Max Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {externalAuditRecords.map((row, idx) => {
                        const catColors: Record<string, string> = { A: '#fecaca', B: '#fde68a', C: '#6ee7b7' };
                        return (
                          <tr key={idx} style={{ background: catColors[row.category] || '#fff', pageBreakInside: 'avoid' }}>
                            <td style={{ ...tdBase, fontWeight: 700 }}>Category {row.category}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.totalNC !== null && row.totalNC !== undefined ? row.totalNC : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.completionNC !== null && row.completionNC !== undefined ? row.completionNC : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.pending !== null && row.pending !== undefined ? row.pending : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.completionPercent !== null && row.completionPercent !== undefined ? `${row.completionPercent.toFixed(2)}%` : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.currentScore !== null && row.currentScore !== undefined ? row.currentScore.toFixed(2) : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.maxScore !== null && row.maxScore !== undefined ? row.maxScore.toFixed(2) : 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              {/* Internal Audit */}
              {internalAuditRecords.length > 0 && (
                <>
                  <div style={{ ...sectionHeaderStyle('#c026d3'), marginTop: '12px' }}>
                    INTERNAL AUDIT - % OF NON COMPLIANCE STATUS REPORT
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: '8px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thBase, width: '20%', textAlign: 'left' }}>Recommendation Category</th>
                        <th style={{ ...thBase, width: '15%' }}>Total NC</th>
                        <th style={{ ...thBase, width: '15%' }}>NC Completion</th>
                        <th style={{ ...thBase, width: '13%' }}>Pending Points</th>
                        <th style={{ ...thBase, width: '15%' }}>% NC Completion</th>
                        <th style={{ ...thBase, width: '12%' }}>Current Score</th>
                        <th style={{ ...thBase, width: '10%' }}>Max Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {internalAuditRecords.map((row, idx) => {
                        const catColors: Record<string, string> = { A: '#fecaca', B: '#fde68a', C: '#6ee7b7' };
                        return (
                          <tr key={idx} style={{ background: catColors[row.category] || '#fff', pageBreakInside: 'avoid' }}>
                            <td style={{ ...tdBase, fontWeight: 700 }}>Category {row.category}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.totalNC !== null && row.totalNC !== undefined ? row.totalNC : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.completionNC !== null && row.completionNC !== undefined ? row.completionNC : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.pending !== null && row.pending !== undefined ? row.pending : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.completionPercent !== null && row.completionPercent !== undefined ? `${row.completionPercent.toFixed(2)}%` : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.currentScore !== null && row.currentScore !== undefined ? row.currentScore.toFixed(2) : 'N/A'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{row.maxScore !== null && row.maxScore !== undefined ? row.maxScore.toFixed(2) : 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}

          {/* ── Section I: FLS Safety Systems ── */}
          <div style={sectionHeaderStyle('#1d4ed8')}>
            (I) Fire and Life Safety Equipment
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...thBase, width: '5%' }}>Sr.No</th>
                <th style={{ ...thBase, width: '30%', textAlign: 'left' }}>Particulars</th>
                <th style={{ ...thBase, width: '8%' }}>Installed (Nos.)</th>
                <th style={{ ...thBase, width: '10%' }}>Inspected &amp; Healthy (Nos.)</th>
                <th style={{ ...thBase, width: '8%' }}>Not Working (Nos.)</th>
                <th style={{ ...thBase, width: '10%' }}>Last Inspection Date</th>
                <th style={{ ...thBase, width: '29%', textAlign: 'left' }}>
                  Remarks for Not Installed / Not Working
                </th>
              </tr>
            </thead>
            <tbody>
              {flsSafetySystems.map((row) => {
                const isKPI = KPI_TABLE1.includes(row.srNo);
                const hasIssue = row.notWorking !== null && row.notWorking > 0;
                const rowStyle: React.CSSProperties = isKPI
                  ? { ...kpiRowStyle, pageBreakInside: 'avoid' }
                  : hasIssue
                    ? { background: '#fff7ed', pageBreakInside: 'avoid' }
                    : { pageBreakInside: 'avoid' };
                return (
                  <tr key={row.srNo} style={rowStyle}>
                    <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: isKPI ? '#0f766e' : undefined }}>
                      {isKPI && <span style={kpiBadge} />}
                      {row.srNo}
                    </td>
                    <td style={{ ...tdBase }}>{row.particulars}</td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.installed !== null && row.installed !== undefined ? row.installed : 'N/A'}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.working !== null && row.working !== undefined ? row.working : 'N/A'}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      <span
                        style={
                          row.notWorking !== null && row.notWorking !== undefined && row.notWorking > 0
                            ? { color: '#dc2626', fontWeight: 700 }
                            : { color: '#16a34a' }
                        }
                      >
                        {row.notWorking !== null && row.notWorking !== undefined ? row.notWorking : 'N/A'}
                      </span>
                    </td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.lastInspectionDate || 'N/A'}
                    </td>
                    <td style={{ ...tdBase }}>{row.remarks || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Section II: MEP-FLS Infrastructure ── */}
          <div
            style={{
              ...sectionHeaderStyle('#15803d'),
              pageBreakBefore: 'always',
              breakBefore: 'page',
            }}
          >
            (II) Fire and Life Safety System
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...thBase, width: '5%' }}>Sr.No</th>
                <th style={{ ...thBase, width: '15%', textAlign: 'left' }}>Systems</th>
                <th style={{ ...thBase, width: '7%' }}>Installed (Nos.)</th>
                <th style={{ ...thBase, width: '7%' }}>Working Healthy (Nos.)</th>
                <th style={{ ...thBase, width: '35%', textAlign: 'left' }}>Process</th>
                <th style={{ ...thBase, width: '8%' }}>Status</th>
                <th style={{ ...thBase, width: '23%', textAlign: 'left' }}>
                  Completion Date / Remark
                </th>
              </tr>
            </thead>
            <tbody>
              {mepFLSInfrastructure.map((row) => {
                const isKPI = KPI_TABLE2.includes(row.srNo);
                const rowStyle: React.CSSProperties = isKPI
                  ? { ...kpiRowStyle, pageBreakInside: 'avoid' }
                  : { pageBreakInside: 'avoid' };
                return (
                  <tr key={row.srNo} style={rowStyle}>
                    <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: isKPI ? '#0f766e' : undefined }}>
                      {isKPI && <span style={kpiBadge} />}
                      {row.srNo}
                    </td>
                    <td style={{ ...tdBase }}>{row.systems}</td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.totalInstalledNos !== null && row.totalInstalledNos !== undefined
                        ? row.totalInstalledNos
                        : 'N/A'}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.workingNos !== null && row.workingNos !== undefined ? row.workingNos : 'N/A'}
                    </td>
                    <td style={{ ...tdBase }}>{row.process}</td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      <span style={getStatusStyle(row.status)}>{row.status || 'N/A'}</span>
                    </td>
                    <td style={{ ...tdBase }}>{row.completionDateOrRemark || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Section III: Fire Incident/Drills ── */}
          <div
            style={{
              ...sectionHeaderStyle('#7c3aed'),
              pageBreakBefore: 'always',
              breakBefore: 'page',
            }}
          >
            (III) Fire Incident / Accidents, Drills &amp; Training Programme
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...thBase, width: '5%' }}>Sr.No</th>
                <th style={{ ...thBase, width: '45%', textAlign: 'left' }}>Description</th>
                <th style={{ ...thBase, width: '8%' }}>Actual No.</th>
                <th style={{ ...thBase, width: '8%' }}>Target No.</th>
                <th style={{ ...thBase, width: '8%' }}>Status</th>
                <th style={{ ...thBase, width: '26%', textAlign: 'left' }}>
                  Completion Date / Remark
                </th>
              </tr>
            </thead>
            <tbody>
              {fireIncidentDrills.map((row) => {
                const isKPI = KPI_TABLE3.includes(row.srNo);
                const rowStyle: React.CSSProperties = isKPI
                  ? { ...kpiRowStyle, pageBreakInside: 'avoid' }
                  : { pageBreakInside: 'avoid' };
                return (
                  <tr key={row.srNo} style={rowStyle}>
                    <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: isKPI ? '#0f766e' : undefined }}>
                      {isKPI && <span style={kpiBadge} />}
                      {row.srNo}
                    </td>
                    <td style={{ ...tdBase }}>{row.systems}</td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.actualNo !== null && row.actualNo !== undefined ? row.actualNo : 'N/A'}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      {row.targetNo !== null && row.targetNo !== undefined ? row.targetNo : 'N/A'}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      <span style={getStatusStyle(row.status)}>{row.status || 'N/A'}</span>
                    </td>
                    <td style={{ ...tdBase }}>{row.completionDateOrRemark || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Section IV: ERT Safety Equipment Status ── */}
          {ertSafetyEquipment && ertSafetyEquipment.length > 0 && (
            <>
              <div
                style={{
                  ...sectionHeaderStyle('#ea580c'),
                  pageBreakBefore: 'always',
                  breakBefore: 'page',
                }}
              >
                (IV) ERT Safety Equipment Status
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ ...thBase, width: '5%' }}>Sr.No.</th>
                    <th style={{ ...thBase, width: '28%', textAlign: 'left' }}>Type of Equipment</th>
                    <th style={{ ...thBase, width: '18%', textAlign: 'left' }}>Responsible Department</th>
                    <th style={{ ...thBase, width: '8%' }}>Total Quantity</th>
                    <th style={{ ...thBase, width: '9%' }}>Condition</th>
                    <th style={{ ...thBase, width: '12%' }}>Last Inspection Date</th>
                    <th style={{ ...thBase, width: '20%', textAlign: 'left' }}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {ertSafetyEquipment.map((row) => (
                    <tr key={row.srNo} style={{ pageBreakInside: 'avoid' }}>
                      <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600 }}>
                        {row.srNo}
                      </td>
                      <td style={{ ...tdBase }}>{row.typeOfEquipment}</td>
                      <td style={{ ...tdBase }}>{row.responsibleDepartment}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        {row.totalQuantity !== null && row.totalQuantity !== undefined
                          ? row.totalQuantity
                          : 'N/A'}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={getConditionStyle(row.condition)}>
                          {row.condition || 'N/A'}
                        </span>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        {row.lastInspectionDate || 'N/A'}
                      </td>
                      <td style={{ ...tdBase }}>{row.remarks || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* ── Section V: Incident Summary ── */}
          {incidentSummary && incidentSummary.length > 0 && (
            <>
              <div
                style={{
                  ...sectionHeaderStyle('#48b0d4'),
                  pageBreakBefore: 'always',
                  breakBefore: 'page',
                }}
              >
                (V) Incident Summary – for the Month (Yearly)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ ...thBase, width: '40%', textAlign: 'left' }}>Description</th>
                    <th style={{ ...thBase, width: '20%' }}>Count</th>
                    <th style={{ ...thBase, width: '40%', textAlign: 'left' }}>Remarks (if any)</th>
                  </tr>
                </thead>
                <tbody>
                  {incidentSummary.map((row, idx) => (
                    <tr key={idx} style={{ pageBreakInside: 'avoid' }}>
                      <td style={{ ...tdBase }}>{row.description}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        {row.count !== null && row.count !== undefined ? row.count : ''}
                      </td>
                      <td style={{ ...tdBase }}>{row.remarks || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Footer */}
          <div
            style={{
              marginTop: '20px',
              paddingTop: '10px',
              borderTop: '1px solid #e2e8f0',
              fontSize: '10px',
              color: '#94a3b8',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>Fire Safety MIS Checklist — {checklistName}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default MISReportPDF;
